import { memo } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  AuraLayout,
  Button,
  Layout,
  PageTitle,
} from "lola-framework-ui-test";
import { ErrorAlertIcon as ErrorIcon } from "../../../assets/icons";
import { BodyCopy } from "../../../components/BodyCopy";
import { FlowHeroMark } from "../../../components/FlowHeroMark";
import { useStore } from "../../../store/globalState";
import {
  DEFERRED_ORIGINATION_ROUTES,
  FUND_REQUEST_ROUTES,
  M2M_ROUTES,
  PAYOUT_CLAIM_ROUTES,
  REQUEST_ROUTES,
  SEND_ROUTES,
  CASH_DEPOSIT_ROUTES,
} from "../../../constants";
import { ScrollFadeIndicator } from "../../../components/ScrollFadeIndicator";

interface UserStatusPageProps {
  namespace: string;
  titleKey: string;
  messageKey: string;
  supportRoute: string;
}

const UserStatusPage = memo(
  ({ namespace, titleKey, messageKey, supportRoute }: UserStatusPageProps) => {
    const { t } = useTranslation(namespace);
    const { theme } = useStore();
    const navigate = useNavigate();
    const colorConfig = theme.errorView;
    const {
      backgroundBtn,
      textColorBtn,
      title,
      bodyCopy,
      iconColors,
      backgroundIcon,
    } = colorConfig;

    return (
      <AuraLayout colorConfig={colorConfig} className="flow-landing-page">
        <Layout.Content className="landing-section flow-hero-section">
          <ScrollFadeIndicator color={colorConfig.bodyCopy} />

          <section className="flow-hero">

            <FlowHeroMark backgroundIcon={backgroundIcon}>

              <ErrorIcon size={60} colors={iconColors} />

            </FlowHeroMark>
          <section className="landing-container">
            <PageTitle
              highlight={t(titleKey)}
              highlightColor={title}
              secudnary=""
              secudnaryColor={title}
            />
            <BodyCopy textColor={bodyCopy}>{t(messageKey)}</BodyCopy>
          </section>
          </section>
        </Layout.Content>
        <Layout.Footer>
          <section className="footer-section">
            <Button
              size="large"
              background={backgroundBtn}
              color={textColorBtn}
              onClick={() => navigate(supportRoute)}
            >
              {t("common.needHelp")}
            </Button>
          </section>
        </Layout.Footer>
      </AuraLayout>
    );
  }
);

export const RequestUserBlockedPage = memo(() => (
  <UserStatusPage
    namespace="request"
    titleKey="error.requestsDisabledTitle"
    messageKey="error.requestsDisabledMessage"
    supportRoute={REQUEST_ROUTES.SUPPORT}
  />
));

export const SendUserBlockedPage = memo(() => (
  <UserStatusPage
    namespace="send"
    titleKey="error.sendsDisabledTitle"
    messageKey="error.sendsDisabledMessage"
    supportRoute={SEND_ROUTES.SUPPORT}
  />
));

export const DeferredUserBlockedPage = memo(() => (
  <UserStatusPage
    namespace="deferred-origination"
    titleKey="error.transfersDisabledTitle"
    messageKey="error.transfersDisabledMessage"
    supportRoute={DEFERRED_ORIGINATION_ROUTES.SUPPORT}
  />
));

export const FundRequestSubjectBlockedPage = memo(() => (
  <UserStatusPage
    namespace="fund-request"
    titleKey="error.cannotContinueTitle"
    messageKey="error.cannotContinueMessage"
    supportRoute={FUND_REQUEST_ROUTES.SUPPORT}
  />
));

export const FundRequestOriginatorBlockedPage = memo(() => (
  <UserStatusPage
    namespace="fund-request"
    titleKey="error.requestUnavailableTitle"
    messageKey="error.requestUnavailableMessage"
    supportRoute={FUND_REQUEST_ROUTES.SUPPORT}
  />
));

export const PayoutClaimSubjectBlockedPage = memo(() => (
  <UserStatusPage
    namespace="payout-claim"
    titleKey="error.cannotContinueTitle"
    messageKey="error.cannotContinueMessage"
    supportRoute={PAYOUT_CLAIM_ROUTES.SUPPORT}
  />
));

export const PayoutClaimSenderBlockedPage = memo(() => (
  <UserStatusPage
    namespace="payout-claim"
    titleKey="error.claimUnavailableTitle"
    messageKey="error.claimUnavailableMessage"
    supportRoute={PAYOUT_CLAIM_ROUTES.SUPPORT}
  />
));

export const M2MUserBlockedPage = memo(() => (
  <UserStatusPage
    namespace="m2m"
    titleKey="error.transfersDisabledTitle"
    messageKey="error.transfersDisabledMessage"
    supportRoute={M2M_ROUTES.SUPPORT_V2}
  />
));

export const CashDepositUserBlockedPage = memo(() => (
  <UserStatusPage
    namespace="cash-deposit"
    titleKey="error.depositUnavailableTitle"
    messageKey="error.depositUnavailableMessage"
    supportRoute={CASH_DEPOSIT_ROUTES.SUPPORT}
  />
));
